import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { FileText, Calendar, Search, PenLine, Eye, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const MIN_BUSCA = 2;
const MAX_CLIENTES_BUSCA = 12;

const TIPOS_LABEL = {
  preventiva: 'Preventiva',
  corretiva: 'Corretiva',
  nao_autorizado: 'Não autorizado',
  emergencia: 'Emergência',
  instalacao: 'Instalação',
  inspecao: 'Inspeção',
  retirar_equipamento: 'Retirar Equipamento',
  visita_tecnica: 'Visita Técnica',
};

const slugTipo = (t) => String(t || '').trim().toLowerCase();

const normalizar = (texto) => String(texto || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const tokensNome = (nome) => normalizar(nome).split(/[^a-z0-9]+/).filter(Boolean);

const pontuarCliente = (cliente, termo) => {
  if (!termo) return 99;
  const nome = normalizar(cliente.nome);
  const codigo = normalizar(cliente.codigo);
  const serie = normalizar(cliente.numero_serie);
  const tokens = tokensNome(cliente.nome);
  if (serie === termo || codigo === termo) return 0;
  if (tokens.some((t) => t === termo)) return 1;
  if (tokens.some((t) => t.startsWith(termo))) return 2;
  if (nome.startsWith(termo) || codigo.startsWith(termo) || serie.startsWith(termo)) return 3;
  if (nome.includes(termo) || codigo.includes(termo) || serie.includes(termo)) return 4;
  return 99;
};

const relatorioBateTexto = (m, clienteQ, serieQ) => {
  if (clienteQ) {
    const bate = [m.cliente_nome, m.cliente_codigo].some((v) => normalizar(v).includes(clienteQ));
    if (!bate) return false;
  }
  if (serieQ) {
    const bate = [m.equipamento_numero_serie, m.equipamento_tag, m.m8_equipamento_nome]
      .some((v) => normalizar(v).includes(serieQ));
    if (!bate) return false;
  }
  return true;
};

const RelatoriosManutencao = () => {
  const { user } = useAuth();
  const permissoes = user?.funcao?.permissoes;
  const podeComplementos = !permissoes || !permissoes.length || permissoes.includes('complementos');
  const [manutencoes, setManutencoes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pronto, setPronto] = useState(false);
  const [tecnicos, setTecnicos] = useState([]);
  const [periodo, setPeriodo] = useState({
    data_inicio: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    data_fim: new Date().toISOString().split('T')[0]
  });
  const [clienteBusca, setClienteBusca] = useState('');
  const [numeroSerie, setNumeroSerie] = useState('');
  const [tecnicoId, setTecnicoId] = useState('');
  const [tipoServico, setTipoServico] = useState('');
  const [tiposCadastrados, setTiposCadastrados] = useState([]);
  const [clientesEncontrados, setClientesEncontrados] = useState([]);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const loadOptions = async () => {
      const [tecnicosRes, tiposRes] = await Promise.allSettled([
        api.get('/users', { params: { funcao: 'tecnico' } }),
        api.get('/tipos-servico-rota'),
      ]);
      // opções de filtro falham em silêncio
      if (tecnicosRes.status === 'fulfilled') setTecnicos(tecnicosRes.value.data?.users || []);
      if (tiposRes.status === 'fulfilled' && Array.isArray(tiposRes.value.data)) setTiposCadastrados(tiposRes.value.data);
    };
    loadOptions();
  }, []);

  useEffect(() => {
    const clienteQ = normalizar(clienteBusca.trim());
    const serieQ = normalizar(numeroSerie.trim());
    const temTexto = clienteQ.length >= MIN_BUSCA || serieQ.length >= MIN_BUSCA;
    const delay = temTexto ? 350 : 0;
    const reqId = ++reqIdRef.current;

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const baseParams = {
          data_inicio: periodo.data_inicio,
          data_fim: periodo.data_fim,
          limit: 500,
          resumo: true,
        };
        if (tecnicoId) baseParams.tecnico_id = tecnicoId;
        if (tipoServico) baseParams.tipo_servico = tipoServico;

        if (!temTexto) {
          const response = await api.get('/manutencoes', { params: baseParams });
          if (reqId !== reqIdRef.current) return;
          setClientesEncontrados([]);
          setManutencoes(response.data.manutencoes || []);
          setTotal(response.data.total || 0);
          return;
        }

        const termoCadastro = (serieQ || clienteQ);
        let encontrados = [];
        try {
          const clientesRes = await api.get('/clientes', {
            params: { search: serieQ || clienteBusca.trim(), limit: 50 },
          });
          encontrados = (clientesRes.data?.clientes || [])
            .map((c) => ({ c, p: pontuarCliente(c, termoCadastro) }))
            .filter(({ c, p }) => {
              if (p >= 99) return false;
              if (clienteQ && pontuarCliente(c, clienteQ) >= 99) return false;
              if (serieQ) {
                const naSerie = normalizar(c.numero_serie).includes(serieQ);
                const noNome = normalizar(c.nome).includes(serieQ);
                if (!naSerie && !noNome) return false;
              }
              return true;
            })
            .sort((a, b) => a.p - b.p || String(a.c.nome || '').localeCompare(b.c.nome || ''))
            .map(({ c }) => c);
        } catch {
          encontrados = [];
        }

        const escolhidos = encontrados.slice(0, MAX_CLIENTES_BUSCA);
        const idsEscolhidos = new Set(escolhidos.map((c) => c.id));
        const idsParam = escolhidos.map((c) => c.id).join(',');

        const periodoRes = await api.get('/manutencoes', {
          params: {
            ...baseParams,
            cliente_ids: idsParam || undefined,
            cliente: clienteBusca.trim() || undefined,
            numero_serie: numeroSerie.trim() || undefined,
          },
        });
        const mapa = new Map();
        for (const m of periodoRes.data?.manutencoes || []) {
          if (idsEscolhidos.has(m.cliente_id) || relatorioBateTexto(m, clienteQ, serieQ)) {
            mapa.set(m.id, m);
          }
        }

        const lista = [...mapa.values()].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        if (reqId !== reqIdRef.current) return;
        setClientesEncontrados(escolhidos);
        setManutencoes(lista);
        setTotal(lista.length);
      } catch (error) {
        if (reqId !== reqIdRef.current) return;
        toast.error(error.response?.data?.detail || 'Erro ao carregar relatórios');
        setManutencoes([]);
        setClientesEncontrados([]);
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setPronto(true);
        }
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [periodo.data_inicio, periodo.data_fim, tecnicoId, tipoServico, clienteBusca, numeroSerie]);

  const temFiltroTexto = Boolean(clienteBusca.trim() || numeroSerie.trim());
  const buscaCurta = temFiltroTexto && clienteBusca.trim().length < MIN_BUSCA && numeroSerie.trim().length < MIN_BUSCA;

  const labelsTipo = { ...TIPOS_LABEL };
  for (const t of tiposCadastrados) {
    const slug = slugTipo(t.slug);
    if (slug) labelsTipo[slug] = t.label || labelsTipo[slug] || slug;
  }
  for (const m of manutencoes) {
    const slug = slugTipo(m.tipo_servico);
    if (slug && !labelsTipo[slug]) labelsTipo[slug] = m.tipo_servico;
  }
  const opcoesTipo = Object.entries(labelsTipo)
    .map(([slug, label]) => ({ slug, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  const temFiltro = temFiltroTexto || Boolean(tipoServico);

  const formatDate = (str) => {
    if (!str) return '—';
    try {
      return new Date(str).toLocaleDateString('pt-BR');
    } catch {
      return str;
    }
  };

  const statusLabel = (s) => {
    const map = { em_andamento: 'Em andamento', concluida: 'Concluída', pendente_assinatura: 'Pendente assinatura', cancelada: 'Cancelada' };
    return map[s] || s;
  };

  const tipoLabel = (t) => labelsTipo[slugTipo(t)] || t || '—';

  const m8Label = (m) => {
    if (m.m8_ordem_servico_id) return `OS ${m.m8_ordem_servico_id}`;
    const s = m.m8_sync_status;
    if (s === 'synced') return 'Sincronizado';
    if (s === 'pending') return 'Pendente';
    if (s === 'processing') return 'Processando';
    if (s === 'failed') return 'Falhou';
    if (s === 'not_mapped') return 'Não mapeado';
    if (s === 'max_retries') return 'Máx tentativas';
    return '—';
  };

  const m8Badge = (m) => {
    if (m.m8_ordem_servico_id) return 'bg-emerald-100 text-emerald-700';
    const s = (m.m8_sync_status || '').toLowerCase();
    if (s === 'pending' || s === 'processing') return 'bg-amber-100 text-amber-700';
    if (s === 'failed' || s === 'not_mapped' || s === 'max_retries') return 'bg-rose-100 text-rose-700';
    return 'bg-slate-100 text-slate-700';
  };

  if (!pronto && loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const nomesClientes = clientesEncontrados
    .slice(0, 4)
    .map((c) => c.nome || c.codigo)
    .filter(Boolean);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
          <FileText className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Relatórios de Manutenção</h1>
          <p className="text-slate-600">Visualizar e editar relatórios (somente edição se não assinado)</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" /> Filtros
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Data início</label>
            <input
              type="date"
              value={periodo.data_inicio}
              onChange={(e) => setPeriodo({ ...periodo, data_inicio: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Data fim</label>
            <input
              type="date"
              value={periodo.data_fim}
              onChange={(e) => setPeriodo({ ...periodo, data_fim: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Cliente</label>
            <input
              type="text"
              value={clienteBusca}
              onChange={(e) => setClienteBusca(e.target.value)}
              placeholder="Ex.: Analin"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Nº de série</label>
            <input
              type="text"
              value={numeroSerie}
              onChange={(e) => setNumeroSerie(e.target.value)}
              placeholder="Ex.: 284"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Técnico</label>
            <select
              value={tecnicoId}
              onChange={(e) => setTecnicoId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none bg-white"
            >
              <option value="">Todos</option>
              {tecnicos.map((t) => (
                <option key={t.id} value={t.id}>{t.name || t.email || t.id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tipo</label>
            <select
              value={tipoServico}
              onChange={(e) => setTipoServico(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none bg-white"
            >
              <option value="">Todos</option>
              {opcoesTipo.map((t) => (
                <option key={t.slug} value={t.slug}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
        {buscaCurta && (
          <p className="text-xs text-slate-500 mt-3">Digite pelo menos 2 caracteres para buscar o cliente.</p>
        )}
        {!buscaCurta && nomesClientes.length > 0 && (
          <p className="text-xs text-slate-500 mt-3">
            Clientes: {nomesClientes.join(', ')}
            {clientesEncontrados.length > nomesClientes.length ? ` e mais ${clientesEncontrados.length - nomesClientes.length}` : ''}
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-sm text-slate-600">
            <Search className="w-4 h-4 inline mr-1" />
            {loading
              ? 'Buscando...'
              : `${total} relatório(s)${temFiltro ? ' encontrados' : ' no período'}`}
          </span>
          <div className="flex items-center gap-2">
          <Link
            to="/relatorios-manutencao-m8"
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50"
          >
            <AlertTriangle className="w-4 h-4" />
            Falhas M8
          </Link>
          {podeComplementos && (
          <Link
            to="/complementos-materiais"
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-sky-200 text-sky-700 hover:bg-sky-50"
          >
            Complementos
          </Link>
          )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Data</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Cliente</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Nº de série</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Técnico</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Tipo</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Assinado</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">M8</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Ação</th>
              </tr>
            </thead>
            <tbody>
              {manutencoes.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-slate-500">
                    {temFiltroTexto && nomesClientes.length > 0
                      ? `Nenhum relatório no período para ${nomesClientes.join(', ')}${tipoServico ? ` do tipo ${tipoLabel(tipoServico)}` : ''}.`
                      : temFiltro
                        ? 'Nenhum relatório com esses filtros.'
                        : 'Nenhum relatório no período.'}
                  </td>
                </tr>
              ) : (
                manutencoes.map((m) => {
                  const assinada = !!m.assinatura;
                  return (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-900">{formatDate(m.created_at)}</td>
                      <td className="py-3 px-4 text-sm text-slate-900">{m.cliente_nome || '—'}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{m.equipamento_numero_serie || '—'}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{m.tecnico_nome || '—'}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{tipoLabel(m.tipo_servico)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${
                          m.status === 'concluida' ? 'bg-emerald-100 text-emerald-700' :
                          m.status === 'em_andamento' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {statusLabel(m.status)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {assinada ? (
                          <span className="text-emerald-600 font-medium">Sim</span>
                        ) : (
                          <span className="text-slate-500">Não</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${m8Badge(m)}`}>
                          {m8Label(m)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Link
                          to={`/relatorios-manutencao/${m.id}`}
                          className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium text-sm"
                        >
                          {assinada ? <Eye className="w-4 h-4" /> : <PenLine className="w-4 h-4" />}
                          {assinada ? 'Ver' : 'Ver / Editar'}
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RelatoriosManutencao;
